// Single source of truth for the tickets PostgREST loaders. Bound as
// element methods (`el.call('pageColumn', colKey)` /
// `el.call('reloadTickets')`) plus exported as plain functions for
// page `onCreate` to call directly.

import { rowToTicket } from './ticketUtils.js'
import { SUPABASE_URL, restHeaders } from '../../../shared/functions/supabaseConfig.js'

export const PAGE_SIZE = 25
export const INITIAL_PAGE_SIZE = 15

const FIELD_EMBED = 'ticket_field_values(field_id,option_name,text_value,number_value,date_value,iteration_id,project_fields(name,data_type))'

const buildFilterSuffix = (filters = {}) => {
  const out = []
  if (filters.search) {
    const q = filters.search.replace(/[%*]/g, '').trim()
    if (q) out.push(`title=ilike.*${encodeURIComponent(q)}*`)
  }
  if (filters.assignee && filters.assignee !== 'all') {
    const AGENT_SUFFIX = encodeURIComponent('%-agent@symbols.app')
    if (filters.assignee === 'me') {
      if (filters.currentUserEmail) out.push(`assignee_email=eq.${encodeURIComponent(filters.currentUserEmail)}`)
    } else if (filters.assignee === 'agents') {
      out.push(`assignee_email=ilike.${AGENT_SUFFIX}`)
    } else if (filters.assignee === 'team') {
      out.push(`assignee_email=not.ilike.${AGENT_SUFFIX}`)
      if (filters.currentUserEmail) out.push(`assignee_email=neq.${encodeURIComponent(filters.currentUserEmail)}`)
    } else {
      // custom email
      out.push(`assignee_email=eq.${encodeURIComponent(filters.assignee)}`)
    }
  }
  if (filters.epic) out.push(`epic_option_id=eq.${encodeURIComponent(filters.epic)}`)
  if (filters.issueType) out.push(`issue_type=eq.${encodeURIComponent(filters.issueType)}`)
  return out.length ? '&' + out.join('&') : ''
}

export const fetchColumnPage = async (colKey, offset, limit = PAGE_SIZE, filters = {}) => {
  const url = `${SUPABASE_URL}/rest/v1/tickets?column_key=eq.${encodeURIComponent(colKey)}&deleted=eq.false&select=*,${FIELD_EMBED}&order=position&offset=${offset}&limit=${limit}${buildFilterSuffix(filters)}`
  const res = await fetch(url, { headers: restHeaders() })
  if (!res.ok) return []
  return res.json()
}

// Lightweight feed of recently-updated tickets — used by the General
// and My View pages so non-engineering stakeholders see fresh activity
// without paying for the full board's per-column pagination.
export const fetchRecentTickets = async (limit = 20, extraFilter = '') => {
  const url = `${SUPABASE_URL}/rest/v1/tickets?deleted=eq.false${extraFilter}&select=id,title,body,column_key,priority,issue_type,epic_name,repository,number,state,labels,assignee_email,reporter_email,updated_at,created_at&order=updated_at.desc&limit=${limit}`
  const res = await fetch(url, { headers: restHeaders() })
  if (!res.ok) return []
  return res.json()
}

// Tickets assigned to a single user — drives the My View page. Same
// shape as fetchRecentTickets so the consumer can swap them.
// Default limit bumped to 100 (from 50) so ASK-USER / awaiting-approval
// items near the end of a large queue are not silently truncated.
export const fetchTicketsAssignedTo = async (email, limit = 100) => {
  if (!email) return []
  const url = `${SUPABASE_URL}/rest/v1/tickets?assignee_email=eq.${encodeURIComponent(email)}&deleted=eq.false&select=id,title,body,column_key,priority,issue_type,epic_name,repository,number,state,labels,assignee_email,reporter_email,updated_at,created_at&order=updated_at.desc&limit=${limit}`
  const res = await fetch(url, { headers: restHeaders() })
  if (!res.ok) return []
  return res.json()
}

// Project Field options (`name='Epic'`, etc.) → flat option array.
// Each option has `{ id, name, color, description }`.
export const fetchProjectFieldOptions = async (fieldName) => {
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/project_fields?name=eq.${encodeURIComponent(fieldName)}&select=options`,
    { headers: restHeaders() }
  )
  if (!res.ok) return []
  const rows = await res.json()
  const opts = rows[0]?.options
  return Array.isArray(opts) ? opts : (opts?.options || [])
}

// Per-epic count + per-column breakdown for the Epics index. Returns
// `{ epicName: { total, byColumn } }`. Prefers the pre-aggregated
// `ticket_epic_counts` view (migration 0017) — one round-trip for the
// whole index — and falls back to client-side aggregation from the
// `tickets` table when the view is missing (migration not applied,
// fresh DB, etc.) so the Epics page renders real counts regardless.
export const fetchEpicCounts = async (epicNames) => {
  const out = {}
  for (const name of (epicNames || [])) out[name] = { total: 0, byColumn: {} }

  // Fast path: pre-aggregated view (one SELECT, server-side grouped).
  const viewRes = await fetch(
    `${SUPABASE_URL}/rest/v1/ticket_epic_counts?select=epic_name,total,by_column`,
    { headers: restHeaders() }
  )
  if (viewRes.ok) {
    const rows = await viewRes.json()
    for (const r of rows) {
      if (!r.epic_name) continue
      out[r.epic_name] = {
        total: r.total || 0,
        byColumn: r.by_column || {}
      }
    }
    return out
  }

  // Fallback: view missing (404) — fetch the raw rows and aggregate in JS.
  // Scoped to non-null epic_name so the payload stays tight, and bumped to
  // a generous limit because PostgREST defaults to 1000 rows per request.
  const ticketsRes = await fetch(
    `${SUPABASE_URL}/rest/v1/tickets?deleted=eq.false&epic_name=not.is.null&select=epic_name,column_key&limit=10000`,
    { headers: restHeaders() }
  )
  if (!ticketsRes.ok) return out
  const tickets = await ticketsRes.json()
  for (const t of tickets) {
    if (!t.epic_name) continue
    if (!out[t.epic_name]) out[t.epic_name] = { total: 0, byColumn: {} }
    out[t.epic_name].total += 1
    if (t.column_key) {
      out[t.epic_name].byColumn[t.column_key] =
        (out[t.epic_name].byColumn[t.column_key] || 0) + 1
    }
  }
  return out
}

// Per-column row count. Caller may pass an explicit `colKeys` array
// (typically `root.columns.map(c => c.key)`) to skip the otherwise-
// extra `ticket_columns` round-trip.
export const fetchColumnCounts = async (filters = {}, colKeys = null) => {
  let keys = colKeys
  if (!keys) {
    const cols = await fetch(
      `${SUPABASE_URL}/rest/v1/ticket_columns?select=key&order=position`,
      { headers: restHeaders() }
    ).then(r => r.json())
    keys = (cols || []).map(c => c.key)
  }
  const out = {}
  await Promise.all(keys.map(async (key) => {
    const url = `${SUPABASE_URL}/rest/v1/tickets?column_key=eq.${encodeURIComponent(key)}&deleted=eq.false&select=id${buildFilterSuffix(filters)}`
    const res = await fetch(url, {
      headers: { ...restHeaders(), Prefer: 'count=exact', Range: '0-0' }
    })
    const cr = res.headers.get('content-range') || '0-0/0'
    out[key] = parseInt(cr.split('/')[1] || '0', 10)
  }))
  return out
}

const filtersFromRoot = (root) => ({
  search: root?.searchQuery || '',
  assignee: root?.filterAssignee || 'all',
  epic: root?.filterEpic || null,
  issueType: root?.filterIssueType || null,
  currentUserEmail: root?.currentUserEmail
})

export const pageColumn = async function (colKey) {
  const root = this.state?.root
  if (!root) return
  const loaded = (root.loadedByColumn || {})[colKey] || 0
  const total = (root.totalByColumn || {})[colKey] || 0
  if (loaded >= total) return
  if ((root.loadingColumn || {})[colKey]) return
  root.update({ loadingColumn: { ...(root.loadingColumn || {}), [colKey]: true } })
  try {
    const rows = await fetchColumnPage(colKey, loaded, PAGE_SIZE, filtersFromRoot(root))
    const mapped = rows.map(rowToTicket)
    root.update({
      tickets: [...(root.tickets || []), ...mapped],
      loadedByColumn: { ...(root.loadedByColumn || {}), [colKey]: loaded + mapped.length },
      loadingColumn: { ...(root.loadingColumn || {}), [colKey]: false }
    })
  } catch (e) {
    console.error('[pageColumn]', e?.message || e)
    root.update({ loadingColumn: { ...(root.loadingColumn || {}), [colKey]: false } })
  }
}

export const reloadTickets = async function () {
  const root = this.state?.root
  if (!root) return
  const filters = filtersFromRoot(root)
  const colKeys = (root.columns || []).map(c => c.key)
  const totals = await fetchColumnCounts(filters, colKeys.length ? colKeys : null)
  root.update({ totalByColumn: totals, tickets: [], loadedByColumn: {} })
  const first = await Promise.all(
    Object.keys(totals).map(async (colKey) => {
      if (!totals[colKey]) return { colKey, rows: [] }
      const rows = await fetchColumnPage(colKey, 0, INITIAL_PAGE_SIZE, filters)
      return { colKey, rows }
    })
  )
  const merged = []
  const loaded = {}
  for (const { colKey, rows } of first) {
    merged.push(...rows.map(rowToTicket))
    loaded[colKey] = rows.length
  }
  root.update({ tickets: merged, loadedByColumn: loaded })
}
